/* Keeping an activity's code when the plan is imported again.
 *
 * The first thing asked of this schedule was that every activity carry an ID
 * that stays with it for life. A replace import quietly broke that: the old
 * rows are retired, the new ones are written, and the database mints fresh
 * codes for them because nothing told it otherwise. A1000 came back as A6550.
 *
 * Nothing would have looked wrong. The baseline is stored against codes, so a
 * re-import would have left every milestone matching nothing, the movement
 * column blank, and the page reading as though the plan had not moved. Silence
 * is the worst failure available here, which is why this is worth a file of
 * its own rather than a line in the importer.
 *
 * Matching runs in three passes, strongest key first. A code is handed out at
 * most once and an activity claims at most one. What matches nothing gets no
 * code and the database mints a fresh one, which is right: an activity that is
 * genuinely new deserves a new code rather than a dead one recycled.
 *
 * The order of the passes is about what each key is worth:
 *
 *   WBS and title together are close to an identity.
 *   Title alone comes next, because a title survives renumbering and a WBS
 *     does not. "Milestone" is the title of eleven rows in the WAPA plan, so
 *     it is trusted only where it appears exactly once on each side.
 *   WBS alone comes last, and the order matters. Insert one row near the top
 *     of a plan and every WBS below it shifts up by one, so the number that
 *     used to mean "Meter Delivery Begins" now means the row above it. Tried
 *     before the title, this pass confidently hands an activity's history to
 *     its neighbour. Tried after, it only ever picks up activities that were
 *     renamed, which is what it is for.
 *
 * Ambiguity is always declined rather than guessed. A wrong code is worse than
 * a new one: a new code loses the history of one activity, a wrong code
 * silently attributes one activity's history to another.
 */

const norm = (s) => String(s ?? "").trim().toLowerCase().replace(/\s+/g, " ");

/* Keys that appear more than once are dropped rather than resolved. */
function unambiguous(items, key, skip) {
  const found = new Map();
  const seenTwice = new Set();
  items.forEach((item, at) => {
    if (skip(item, at)) return;
    const k = key(item);
    if (!k) return;
    if (found.has(k)) seenTwice.add(k);
    else found.set(k, at);
  });
  for (const k of seenTwice) found.delete(k);
  return found;
}

/**
 * existing  live rows about to be retired: { activity_code, activity_id, title }
 * incoming  parsed activities, in the order they will be written
 * returns   Map of incoming index -> the activity code it should keep
 */
export function carryCodes(existing = [], incoming = []) {
  const keep = new Map();
  const usedCode = new Set();
  const matched = new Set();

  const pass = (key) => {
    const before = unambiguous(existing, key,
      (e) => !e.activity_code || usedCode.has(e.activity_code));
    const after = unambiguous(incoming, key,
      (_, at) => matched.has(at));

    for (const [k, at] of after) {
      if (!before.has(k)) continue;
      const code = existing[before.get(k)].activity_code;
      keep.set(at, code);
      usedCode.add(code);
      matched.add(at);
    }
  };

  pass((r) => (norm(r.activity_id) && norm(r.title)
    ? `${norm(r.activity_id)}|${norm(r.title)}` : ""));
  pass((r) => norm(r.title));
  pass((r) => norm(r.activity_id));

  return keep;
}
